import React, {useCallback, useContext, useEffect, useMemo, useRef, useState} from 'react';
import PropTypes from "prop-types";
import {useDispatch, useSelector} from "react-redux";
import Typography from "@mui/material/Typography";
import {
    initializeGroupEditorCreate,
    initializeGroupEditorEdit,
    updateGroupTemplate,
    clearGroupEditor,
    validateGroupNameAndQualifierUnique,
    setGroupLikertMode,
    addGroupQuestion,
    addGroupShowGroupKeys,
    setGroupShowGroupKeys
} from "../../../actions/groupEditorActions";
import {
    getGroupTemplate,
    addGroupTemplateQuestionKey,
    addGroupTemplateShowGroupKeys,
    setGroupTemplateShowGroupKeys,
    createGroupTemplate
} from "../../../actions/groupTemplateActions";
import {
    getQuestionTemplate,
    createQuestionTemplate,
    saveQuestionTemplate
} from "../../../actions/questionTemplateActions";
import {expanderClear} from "../../../actions/expanderActions";
import ActionEditorHook from "../actions/ActionEditorHook";
import {createActionTemplate} from "../../../actions/actionTemplateActions";
import {addDrawerNameToStack, removeDrawerNameToStack} from "../../../actions/drawerStackAction";
import QuestionEditor from "../questions/QuestionEditorHook";
import QuestionViewerExpandableHook from "../questions/QuestionViewerExpandableHook";
import {notificationOpen} from "../../../actions/notificationActions";
import {templateSaveSucceeded} from "../../../actions/templateSaveActions";
import useDeferredTemplateSave from "../../../hooks/useDeferredTemplateSave";
import ArgoPortalPreviewHook from "../../common/ArgoPortalPreviewHook";
import ArgoMetadataEditorHook from "../../common/ArgoMetadataEditorHook";
import ArgoSelectExistingHook from "../../common/ArgoSelectExistingHook";
import ArgoTextEntryWithCustomOverrideHook from "../../common/ArgoTextEntryWithCustomOverrideHook";
import ArgoLikertAnswerGeneratorHook from "../../common/ArgoLikertAnswerGeneratorHook";
import ArgoReviewTemplateHook from "../../common/ArgoReviewTemplateHook";
import ArgoCheckboxHook from "../../common/ArgoCheckboxHook";
import ArgoToolbarHook from "../../common/ArgoToolbarHook";
import ArgoTextFieldHook from "../../common/ArgoTextFieldHook";
import ArgoCustomLikertInputStylingHook from "../../common/ArgoCustomLikertInputStylingHook";
import * as validate from "../../../utilities/validate";
import * as expander from "../../../utilities/expander";
import * as metadata from "../../../utilities/metadata";
import * as metadataTemplateTypes from "../../../constants/metadataTemplateTypes";
import * as questionTypes from "../../../constants/questionTypeConstants";
import * as icon from "../../../constants/iconConstants";
import * as TEMPLATE from "../../../constants/templateConstants";
import * as portalShared from "../../../portal/shared/metadataConstantsAndUtilities"
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import UserContext from "../../../UserContext";
import {Tooltip} from "@mui/material";
import GroupViewerExpandableHook from "./GroupViewerExpandableHook";
import {createEditorOrigin, EDITOR_TYPES} from "../../../utilities/editorInstanceOwnership";



const QUESTION_DEPTH = 0;

const GroupEditor = (props) => {
    const dispatch = useDispatch();

    // Redux selectors (can be overridden by ownProps to match connect() merge behavior)
    const storeOptions = useSelector(state => state.groupEditor.options);
    const storeTemplate = useSelector(state => state.groupEditor.template);
    const groupTemplates = useSelector(state => state.groupTemplate);
    const storeTemplateOriginal = useSelector(state => state.groupTemplate[props.templateKey]);
    const questionKeys = useSelector(state => state.groupEditor.questionKeys);
    const storeAnswerOptionTemplates = useSelector(state => state.groupEditor.template?.answerOptionTemplates);
    const storeQuestionTemplateList = useSelector(state => state.questionTemplateList);
    const questionTemplates = useSelector(state => state.questionTemplate);
    const storeMetadataDefinition = useSelector(state => state.metadataDefinition[metadataTemplateTypes.GroupTemplate]);
    const storeAnswerIndex = useSelector(state => state.showGroup.answerIndex);
    const editorInstanceId = useSelector(state => state.groupEditor.instanceId);

    const options = props.options ?? storeOptions;
    const template = props.template ?? storeTemplate;

    const templateOriginal = props.templateOriginal ?? storeTemplateOriginal;

    const answerOptionTemplates = props.answerOptionTemplates ?? storeAnswerOptionTemplates ?? template?.answerOptionTemplates;
    const questionTemplateList = props.questionTemplateList ?? storeQuestionTemplateList;

    const metadataDefinition = props.metadataDefinition ?? storeMetadataDefinition;
    const answerIndex = props.answerIndex ?? storeAnswerIndex;

    const [state, setState] = useState({
        expander: {},
        expandAll: true,
        isThisAnExistingQuestion: "0",
        isLikert: false,
        likertIndentPixels: "40",
        questionTemplateSearch: "",
        questionDetail: null,
        nameErrorText: " ",
        answerErrorText: " ",
        metadataErrorText: "",
        nameQualifierErrorText: " ",
        descErrorText: " ",
        descriptionErrorText: " ",
        KMCErrorText: "",
        KMCErrorQuestionNames: [],
        answersError: null,
        isLoading: false,
        isEditCreate: false,
        toolbarErrorText: "",
        questionList: [],
        editQuestionKey: null,
        likertErrorText: null,
        nameDisplay: {...metadata.defaultCustomMetadataObj},
        descDisplay: {...metadata.defaultCustomMetadataObj},
        allAccordionsExpanded: false,
        questionsLoaded: true,
        showActionEditor: false,
        showGroupEditor: false,
        headerInitializer: "",
        openQuestionEditor: false,
        questionTemplate: {},
        metadataSet: false,
        initialScroll: false,
        openQuestionReview: false,
        selectedQuestionInfoId: "",
        selectedQuestionInfoKey: "",
        selectedQuestionInfoTemplate: null,
        openGroupReview: false,
        selectedGroupInfoId: "",
        selectedGroupInfoKey: "",
        selectedGroupInfoTemplate: null,
    });

    const theme = useContext(UserContext);

    const localStateRef = useRef(state);
    useEffect(() => {
        localStateRef.current = state;
    }, [state]);

    // componentDidMount: initial scroll + init
    const didInitRef = useRef(false);

    useEffect(() => {
        dispatch(addDrawerNameToStack(props.createMode ? "Create" : "Edit", "Group"))

        // componentWillUnmount equivalent
        return () => {
            dispatch(expanderClear("GroupEditor"));
            dispatch(clearGroupEditor());
            dispatch(removeDrawerNameToStack())
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    },[]);

    const setStateMerged = useCallback((stateObj) => {
        setState(prev => ({...prev, ...stateObj}));
    }, []);

    const pendingMetadataUpdateRef = useRef(null);

    // Allow child components to set errors on this page.
    const setStateCallback = useCallback((stateObj, saveQuestionAfter = false, templateType) => {

        setStateMerged(stateObj);
        if (saveQuestionAfter && templateType === TEMPLATE.TYPE.QUESTION) {
            return dispatch(saveQuestionTemplate(stateObj.questionTemplate)).then(result => {
                if (templateSaveSucceeded(result)) {
                    setStateMerged({openQuestionEditor: false});
                }
                return result;
            });
        }

        return undefined;
    }, [dispatch, setStateMerged]);

    const pageInit = useCallback((currTemplateOriginal) => {
        if (!currTemplateOriginal || !currTemplateOriginal.metadata) return;

        const metadataObj = currTemplateOriginal.metadata;
        const tempNameDisplayObj = metadata.setTextObjSelectedAndValueAndValueObj(metadataObj, portalShared.CUSTOM_DDO_NAME);
        const tempDescDisplayObj = metadata.setTextObjSelectedAndValueAndValueObj(metadataObj, portalShared.CUSTOM_DDO_DESC);

        setStateMerged({
            nameDisplay: metadata.setCustomMetadataObj(tempNameDisplayObj),
            descDisplay: metadata.setCustomMetadataObj(tempDescDisplayObj)
        });
    }, [setStateMerged]);

    const checkForErrorsOnScreen = useCallback((propsForValidate) => {
        const nameErrorText = validate.templateNameAndQualifierUnique(propsForValidate, "Group Name");
        const metadataErrorText = state.metadataErrorText;
        let KMCErrorText = ""
        let KMCErrorQuestionNames = []
        if (questionKeys.length > 0) {
            if ([3, 5, 7].includes(answerOptionTemplates.length)) {
                questionKeys.forEach(key => {
                    if (questionTemplates[key]?.metadata?.["reporting-category-question"] === "Net Promoter Score (NPS)") {
                        KMCErrorText = "Key Metric Classification Mismatch!"
                        KMCErrorQuestionNames.push(questionTemplates[key].name)
                    }
                })
            }

            if (answerOptionTemplates.length === 11) {
                questionKeys.forEach(key => {
                    if (questionTemplates[key]?.metadata?.["reporting-category-question"] !== undefined &&
                        questionTemplates[key]?.metadata?.["reporting-category-question"] !== "Net Promoter Score (NPS)") {
                        KMCErrorText = "Key Metric Classification Mismatch!"
                        KMCErrorQuestionNames.push(questionTemplates[key].name)
                    }
                })
            }
        }

        let toolbarErrorText = "";

        if (nameErrorText !== " ") {
            toolbarErrorText = nameErrorText;
        }

        if (metadataErrorText !== "") {
            toolbarErrorText = toolbarErrorText === "" ? metadataErrorText : toolbarErrorText + ",  " + metadataErrorText;
        }

        if (state.isLikert) {
            if (propsForValidate.template?.hasOwnProperty("answerOptionTemplates")) {
                if (propsForValidate.template.answerOptionTemplates.length === 0) {
                    const likertErrorText = "Likert groups require Answers.";
                    toolbarErrorText = toolbarErrorText === "" ? likertErrorText : toolbarErrorText + ",  " + likertErrorText;
                }

                if ((propsForValidate.questionKeys || []).length === 0) {
                    const likertErrorText = "Likert groups require at least one Question!";
                    toolbarErrorText = toolbarErrorText === "" ? likertErrorText : toolbarErrorText + ",  " + likertErrorText;
                }
            }
        }

        if (KMCErrorText !== "") {
            toolbarErrorText = toolbarErrorText === "" ? KMCErrorText : toolbarErrorText + ",  " + KMCErrorText;
        }

        setStateMerged({nameErrorText, metadataErrorText, KMCErrorText, KMCErrorQuestionNames, toolbarErrorText});
    }, [setStateMerged, state.isLikert, state.metadataErrorText, Object.keys(questionTemplates).length, answerOptionTemplates.length, questionKeys.length]);

    const setMetadataErrorText = useCallback((metadataErrorText) => {
        setStateMerged({metadataErrorText, metadataSet: false});
    }, [setStateMerged]);

    const metadataChanged = useCallback((updatedTemplate, depth) => {
        pendingMetadataUpdateRef.current = {updatedTemplate, depth};
        setStateMerged({metadataSet: true});
    }, [setStateMerged]);


    // After metadataSet flips true, run the queued updateGroupTemplate (preserves class timing intent).
    useEffect(() => {
        if (!state.metadataSet) return;
        if (!pendingMetadataUpdateRef.current) return;
        const {updatedTemplate, depth} = pendingMetadataUpdateRef.current;
        pendingMetadataUpdateRef.current = null;
        dispatch(updateGroupTemplate(updatedTemplate, depth));
    }, [dispatch, state.metadataSet]);

    useEffect(() => {

        if (didInitRef.current) return;
        didInitRef.current = true;

        const page = document.getElementById("GroupEditor-height-div");
        if (page) {
            page.scrollIntoView({block: 'end', behavior: 'smooth'});
        }

        if (props.templateKey || props.templateId) {
            const groupTemplateAtKey = groupTemplates?.[props.templateKey];

            if (groupTemplateAtKey && (!props.templateId || (groupTemplateAtKey.id === props.templateId))) {
                setStateMerged({isLikert: (groupTemplateAtKey.questionGroupType === "LIKERT")});
                dispatch(initializeGroupEditorEdit(groupTemplateAtKey));
                pageInit(groupTemplateAtKey);
            } else if (props.templateKey) {
                setStateMerged({isLoading: true});
                dispatch(getGroupTemplate(props.templateKey, props.templateId));
            }
        } else {
            dispatch(initializeGroupEditorCreate(props.createInitialTemplate));
        }

        props.groupExpanderSetStateCallback({openGroupEditorComplete: true});
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dispatch, groupTemplates, pageInit, props.createInitialTemplate, props.groupExpanderSetStateCallback, props.templateId, props.templateKey, setStateMerged]);


    useEffect(() => {
        // Get showGroupKeys templates
        //debugger; // SSSSS Show Group Testing
        if (template.hasOwnProperty("showGroupKeys") && template.showGroupKeys && template.showGroupKeys.length) {
            for (let i = 0; i < template.showGroupKeys.length; i++) {
                dispatch(getGroupTemplate(template.showGroupKeys[i]));
            }
        }

    }, [template, dispatch]);

    // componentWillReceiveProps: initial scroll to target
    useEffect(() => {
        if (state.initialScroll) return;
        const scrollTo = (props.scrollTo === "") ? "GroupEditor-height-div" : props.scrollTo;
        const page = document.getElementById(scrollTo);
        if (page) {
            page.scrollIntoView({block: 'end', behavior: 'smooth'});
            setStateMerged({initialScroll: true});
        }
    }, [props.scrollTo, setStateMerged, state.initialScroll]);

    // componentWillReceiveProps: keep expander + likert in sync (when metadataSet is false)
    useEffect(() => {
        if (state.metadataSet) return;

        const groupTemplateAtKey = groupTemplates?.[props.templateKey];
        let tempIsLikert = state.isLikert;
        if (groupTemplateAtKey) {
            tempIsLikert = (groupTemplateAtKey.questionGroupType === "LIKERT");
        }

        setState(previousState => ({
            ...previousState,
            expander: expander.presetExpander(questionKeys, previousState.expander),
            isLikert: tempIsLikert
        }));
    }, [groupTemplates, props.templateKey, questionKeys, state.isLikert, state.metadataSet]);

    // componentWillReceiveProps: handle edit template load completion
    useEffect(() => {

        const isTemplateLoaded = (templateOriginal && templateOriginal.templateKey === props.templateKey
            && template?.templateKey !== props.templateKey);

        const editTemplateReady = template?.id !== props.templateId &&
            (groupTemplates?.[props.templateKey] && groupTemplates[props.templateKey].id === props.templateId);

        if (isTemplateLoaded && editTemplateReady) {
            setStateMerged({isLoading: false});
            dispatch(initializeGroupEditorEdit(templateOriginal));
            pageInit(templateOriginal);
        }
    }, [dispatch, groupTemplates, pageInit, props.templateId, props.templateKey, setStateMerged, template, templateOriginal]);

    // componentWillReceiveProps: ensure question templates are loaded
    useEffect(() => {
        (questionKeys || []).forEach((templateKey, index) => {
            if (!questionTemplates?.[templateKey]) {
                dispatch(getQuestionTemplate(templateKey, null, true, index));
            }
        });
    }, [dispatch, questionKeys, questionTemplates]);

    // componentWillReceiveProps: build selectable question list
    useEffect(() => {
        const nextQuestionList = [];
        for (let i = 0; i < (questionTemplateList || []).length; i++) {
            if ((questionKeys || []).indexOf(questionTemplateList[i].templateKey) === -1) {
                nextQuestionList.push(questionTemplateList[i]);
            }
        }

        if (!state.metadataSet) {
            setStateMerged({questionList: nextQuestionList});
        }
    }, [questionKeys, questionTemplateList, setStateMerged, state.metadataSet]);


    useEffect(() => {

        let tempGroupTemplate = props.createMode ? template : templateOriginal;

        // Check to see if the questions are loaded
        for (let i = 0; i < questionKeys.length; i++) {

            // Is question loaded
            if (questionTemplates.hasOwnProperty(questionKeys[i])) {

                // See if question has showGroupKeys
                let tempQuestionTemplate = portalShared.cloneObj(questionTemplates[questionKeys[i]]);
                if (!tempQuestionTemplate.showGroupKeys) {
                    tempQuestionTemplate.showGroupKeys = []; // FIX for something setting showGroupKeys to null
                }
                for (let ii = 0; ii < tempQuestionTemplate.showGroupKeys.length; ii++) {

                    // If question has showGroupKeys then make sure groups are loaded and added to group.showGroupKeys
                    if (tempGroupTemplate.showGroupKeys && !tempGroupTemplate.showGroupKeys.includes(tempQuestionTemplate.showGroupKeys[ii])) {
                        // Add Key to category.showGroupKeys
                        if (props.createMode) {
                            dispatch(addGroupShowGroupKeys(tempGroupTemplate, [tempQuestionTemplate.showGroupKeys[ii]]));

                        } else {

                            dispatch(addGroupTemplateShowGroupKeys(tempGroupTemplate, [tempQuestionTemplate.showGroupKeys[ii]]));
                        }
                    }

                    // Check to see if group is loaded
                    if (!groupTemplates.hasOwnProperty(tempQuestionTemplate.showGroupKeys[ii])) {
                        dispatch(getGroupTemplate(tempQuestionTemplate.showGroupKeys[ii])); // Show Group needs to be loaded for expander to display
                    }

                }
            } else {
                // Question not loaded so get it
                dispatch(getQuestionTemplate(questionKeys[i])); // get the question to check for showGroupKeys
            }
        }


    }, [template, groupTemplates, templateOriginal, questionTemplates, JSON.stringify(questionKeys)]);



    // Keep toolbar errors in sync
    const propsForValidate = useMemo(() => ({
        ...props,
        options,
        template,
        questionKeys,
        answerOptionTemplates,
        metadataDefinition,
        answerIndex
    }), [answerIndex, answerOptionTemplates, metadataDefinition, options, props, questionKeys, template]);

    useEffect(() => {
        if (!template) return;
        checkForErrorsOnScreen(propsForValidate);
    }, [checkForErrorsOnScreen, propsForValidate, template]);

    // ----------------------------------------------------------------------------------
    // Handlers
    // ----------------------------------------------------------------------------------

    const removeQuestion = (questionTemplateKey) => {

        let groupTemplate = props.createMode ? template : templateOriginal;
        let showGroupKeys = [];

        // Loop through all questions still assigned and build a list of showGroupKeys
        for (let i = 0; i < template.questionKeys.length; i++) {
            if (template.questionKeys[i] !== questionTemplateKey) {
                // From any remaining questions assigned add any showGroupKeys
                for (let ii = 0; ii < questionTemplates[template.questionKeys[i]].showGroupKeys.length; ii++) {
                    showGroupKeys.push(questionTemplates[template.questionKeys[i]].showGroupKeys[ii]);
                }
            }
        }

        if (props.createMode) {
            dispatch(setGroupShowGroupKeys(questionTemplateKey, showGroupKeys));
        } else {
            dispatch(setGroupTemplateShowGroupKeys(groupTemplate, questionTemplateKey, showGroupKeys));
        }

    };

    const saveGroup = useCallback(() => {

        let nextTemplate;
        if (template.hasOwnProperty(props.templateKey)) {
            nextTemplate = {
                ...template[props.templateKey],
                questionKeys: [...(questionKeys || [])]
            };
        }
        else {
            nextTemplate = {
                ...template,
                questionKeys: [...(questionKeys || [])]
            };
        }

        if (props.groupExpanderEdit) {
            return props.groupExpanderSetStateCallback({groupTemplate: nextTemplate}, true, TEMPLATE.TYPE.GROUP);
        }

        return props.save(nextTemplate);
    }, [props, questionKeys, template]);

    const notifyDeferredSave = useCallback(() => {
        dispatch(notificationOpen("Waiting for the new template to finish saving…"));
    }, [dispatch]);
    const saveGroupWhenReady = useDeferredTemplateSave(saveGroup, notifyDeferredSave);

    const handleToolbarButtonClick = useCallback((event, fieldName, buttonTitle) => {
        switch (buttonTitle) {
            case "CANCEL":
                props.cancel();
                break;
            case "DONE":
                break;
            case "SAVE":
                saveGroupWhenReady();
                break;
            case "BACK":
                setStateMerged({openGroupReview: false});
                setStateMerged({openQuestionReview: false});
                break;
            // no default
        }
    }, [props, saveGroupWhenReady, setStateMerged]);

    const handleInputChangeAndUpdateTemplate = useCallback((event, fieldName, value) => {

        const updatedTemplate = {...template};
        updatedTemplate[fieldName] = value;

        dispatch(updateGroupTemplate(updatedTemplate));

        if (fieldName === "name" || fieldName === "nameQualifier") {
            dispatch(validateGroupNameAndQualifierUnique(updatedTemplate.name, updatedTemplate.nameQualifier));
        }
    }, [dispatch, template]);

    const onTextChange = useCallback((event, fieldName) => {
        let value = event.target.value;

        if ((fieldName === "nameQualifier") && (value === "")) {
            value = null;
        }

        handleInputChangeAndUpdateTemplate(event, fieldName, value);
    }, [handleInputChangeAndUpdateTemplate]);

    // const onTextChangeLikertIndentPixels = useCallback((event) => {
    //
    //     const value = event.target.value;
    //
    //     setStateMerged({likertIndentPixels: value});
    //
    //     const updatedTemplate = portalShared.cloneObj(template);
    //     const customGroupObj = JSON.parse(updatedTemplate.metadata[portalShared.CUSTOM_DDO_GRP_FORMAT]);
    //     customGroupObj.likertObj.indent = value + "px";
    //     updatedTemplate.metadata[portalShared.CUSTOM_DDO_GRP_FORMAT] = JSON.stringify(customGroupObj);
    //
    //     dispatch(updateGroupTemplate(updatedTemplate));
    // }, [dispatch, setStateMerged, template]);


    const addSelectedQuestion = useCallback((questionTemplateKey, parentTemplate) => {

        if (!questionKeys.includes(questionTemplateKey.templateKey)) {
            dispatch(getQuestionTemplate(questionTemplateKey.templateKey)); // get the question to check for showGroupKeys

            dispatch(addGroupQuestion(questionTemplateKey.templateKey));
            if (!props.createMode) {
                dispatch(addGroupTemplateQuestionKey(templateOriginal, questionTemplateKey.templateKey));
            }

        }
        setStateMerged({questionTemplateSearch: ""});
    }, [dispatch, questionKeys, setStateMerged, templateOriginal, props.createMode]);


    const saveNewQuestion = useCallback((questionTemplateToSave) => {
        const saveResult = state.editQuestionKey
            ? dispatch(saveQuestionTemplate(questionTemplateToSave))
            : dispatch(createQuestionTemplate(
                questionTemplateToSave,
                QUESTION_DEPTH,
                createEditorOrigin(EDITOR_TYPES.GROUP, editorInstanceId)
            ));

        return saveResult.then(result => {
            if (templateSaveSucceeded(result)) {
                setStateMerged({isThisAnExistingQuestion: "0", questionTemplateSearch: "", editQuestionKey: null});
            }
            return result;
        });
    }, [dispatch, editorInstanceId, setStateMerged, state.editQuestionKey]);

    const closeNewQuestion = useCallback(() => {
        setStateMerged({isThisAnExistingQuestion: "0", questionTemplateSearch: "", editQuestionKey: null});
    }, [setStateMerged]);

    const toggleIsThisAnExistingQuestion = useCallback(() => {
        setStateMerged({
            isThisAnExistingQuestion: "1",
            questionTemplateSearch: state.questionTemplateSearch
        });
    }, [setStateMerged, state.questionTemplateSearch]);

    const onCheckboxChange = useCallback((event) => {
        const isChecked = event.target.checked;
        setStateMerged({isLikert: isChecked});
        dispatch(setGroupLikertMode(template, isChecked));
    }, [dispatch, setStateMerged, template]);


    const answerErrorCallback = useCallback(() => {
        // TODO capture answer errors
    }, []);

    const saveAction = useCallback((actionTemplate) => {
        return dispatch(createActionTemplate(
            {...actionTemplate, answerIndex: state.answerIndex},
            props.parentEditorOrigin
        )).then(result => {
            if (templateSaveSucceeded(result)) {
                setStateMerged({showActionEditor: false, answerIndex: null, headerInitializer: ""});
            }
            return result;
        });
    }, [
        dispatch,
        props.parentEditorOrigin,
        setStateMerged,
        state.answerIndex
    ]);

    const closeActionEditor = useCallback(() => {
        setStateMerged({showActionEditor: false, answerIndex: null, headerInitializer: ""});
    }, [setStateMerged]);

    const actionEditor = useCallback(() => {
        const actionTemplate = {
            name: state.headerInitializer
        };

        return (
            <div className="layout vertical" style={{height: "100%"}}>
                <div className="layout vertical" style={{height: "100%"}}>
                    <ActionEditorHook
                        save={saveAction}
                        cancel={closeActionEditor}
                        createInitialTemplate={actionTemplate}
                        showCreateToggle={state.isCreateActionSelector}
                        width={props.width}
                    />
                </div>
            </div>
        );
    }, [closeActionEditor, props.width, saveAction, state.headerInitializer, state.isCreateActionSelector]);

    const closeShowGroupEditor = useCallback(() => {
        setStateMerged({showGroupEditor: false, answerIndex: null});
    }, [setStateMerged]);

    const saveShowGroup = useCallback((groupTemplateToSave) => {
        return dispatch(createGroupTemplate(
            {...groupTemplateToSave, answerIndex: state.answerIndex},
            props.parentEditorOrigin
        )).then(result => {
            if (templateSaveSucceeded(result)) {
                setStateMerged({showGroupEditor: false, answerIndex: null});
            }
            return result;
        });
    }, [
        dispatch,
        props.parentEditorOrigin,
        setStateMerged,
        state.answerIndex
    ]);

    const groupEditor = useCallback(() => {
        return (
            <div className="layout vertical" style={{height: "100%"}}>
                <div className="layout vertical" style={{height: "100%"}}>
                    <GroupEditor
                        createMode={true}
                        save={saveShowGroup}
                        cancel={closeShowGroupEditor}
                        groupAddShowGroupMode={true}
                        templateMode={"Create Group"}
                        scrollTo="GroupEditor-container-outer-div"
                    />
                </div>
            </div>
        );
    }, [closeShowGroupEditor, saveShowGroup]);

    const questionEditorSave = useCallback(() => {
        return dispatch(saveQuestionTemplate(state.questionTemplate)).then(result => {
            if (templateSaveSucceeded(result)) {
                setStateMerged({openQuestionEditor: false});
            }
            return result;
        });
    }, [dispatch, setStateMerged, state.questionTemplate]);

    const questionEditorCancel = useCallback(() => {
        setStateMerged({openQuestionEditor: false});
    }, [setStateMerged]);

    // ----------------------------------------------------------------------------------
    // Render
    // ----------------------------------------------------------------------------------

    if (state.isLoading) {
        return null;
    }

    if (state.showActionEditor) {
        return actionEditor();
    }

    if (state.showGroupEditor) {
        return groupEditor();
    }

    if (state.openGroupReview) {
        return <ArgoReviewTemplateHook
            templateType={TEMPLATE.TYPE.GROUP}
            templateId={state.selectedGroupInfoId}
            templateKey={state.selectedGroupInfoKey}
            selectedTemplate={state.selectedGroupInfoTemplate}
            selectedTemplateIsGraph={false}
            handleToolbarButtonClickFunction={handleToolbarButtonClick}
            width={props.width}
        />;
    }

    if (state.openQuestionReview) {
        return (
            <ArgoReviewTemplateHook
                templateType={TEMPLATE.TYPE.QUESTION}
                templateKey={state.selectedQuestionInfoKey}
                templateId={state.selectedQuestionInfoId}
                templateObject={state.selectedQuestionInfoTemplate}
                handleToolbarButtonClickFunction={handleToolbarButtonClick}
                width={props.width}
            />
        );
    }

    if (state.isThisAnExistingQuestion === "1") {
        const questionCreateTemplate = {
            name: state.questionTemplateSearch
        };

        if (state.isLikert) {
            questionCreateTemplate.questionType = questionTypes.LIKERT_RADIO;
        }

        return (
            <QuestionEditor
                title="Create Question"
                createMode={true}
                save={saveNewQuestion}
                cancel={closeNewQuestion}
                showCreateToggle={state.isEditCreateSelector}
                isLikert={state.isLikert}
                calledFrom={TEMPLATE.TYPE.GROUP}
                createInitialTemplate={questionCreateTemplate}
                questionDepth={QUESTION_DEPTH}
                templateKey={state.editQuestionKey}
                width={props.width}
            />
        );
    }

    if (state.openQuestionEditor) {

        return (
            <QuestionEditor
                title="Edit Question"
                save={questionEditorSave}
                cancel={questionEditorCancel}
                templateKey={state.questionTemplate.templateKey}
                templateId={state.questionTemplate.id}
                filter={props.filter}
                templateMode={TEMPLATE.MODE.CURRENT}
                questionDepth={QUESTION_DEPTH}
                questionExpanderEdit={true}
                questionExpanderSetStateCallback={setStateCallback}
                width={props.width}
                calledFrom={TEMPLATE.TYPE.GROUP}
                isLikert={state.isLikert}
                scrollTo="GroupEditor-container-outer-div"
            />
        );
    }

    const propsObj = {
        fileName: "GroupEditor",
        onTextChange,
        templateType: TEMPLATE.TYPE.GROUP,
        template,
        answerOptionTemplates,
        templateUpdateFunction: (t, d) => dispatch(updateGroupTemplate(t, d)),
        setStateCallbackFunction: setStateCallback,
        setCustomMetadataFunction: metadata.setCustomMetadata,
        depth: props.depth
    };

    let groupTemplateClone = portalShared.cloneObj(template);
    // if (!props.createMode && templateOriginal) {
    //     groupTemplateClone = portalShared.cloneObj(templateOriginal);
    // }
    groupTemplateClone.questionKeys = questionKeys;

    let likertQuestionIndent = "";
    if (template?.hasOwnProperty("metadata")) {
        if (template.metadata.hasOwnProperty(portalShared.CUSTOM_DDO_GRP_FORMAT)) {
            const customGroupObj = JSON.parse(template.metadata[portalShared.CUSTOM_DDO_GRP_FORMAT]);
            if (customGroupObj.likertObj.indent === 0) {
                likertQuestionIndent = "40";
            } else {
                likertQuestionIndent = customGroupObj.likertObj.indent.replace("px", "");
            }
        }
    }

    return (
        template && didInitRef.current ?
            <div id="GroupEditor-container-outer-div" style={{height: "100%"}}>
                <div id="GroupEditor-ArgoToolbarHook-container-div">
                    <ArgoToolbarHook
                        fileName="GroupEditor"
                        fieldName="save-or-cancel"
                        toolbarErrorText={state.toolbarErrorText}
                        isValidating={options?.isValidating}
                        width={props.width}
                        onClick={handleToolbarButtonClick}
                        icon={
                            <Tooltip title={`Questions: ${state.KMCErrorQuestionNames.join(", ")} ${state.KMCErrorQuestionNames.length > 1 ? "are" : "is"} not compatible with the number of answers`}>
                                <InfoOutlinedIcon style={{color: theme.palette.errorColor, fontSize: "12px"}}/>
                            </Tooltip>
                        }
                    />
                </div>

                <div id="GroupEditor-height-div" style={{height: "50px"}}></div>

                <div id="GroupEditor-page-div" style={{height: "100%", paddingRight: "40px"}}>

                    <div id="GroupEditor-container-inner-div" style={{flexGrow: "2", paddingLeft: "32px"}}>

                        <div>
                            {props.title === "" ? "" :
                                <div>
                                    <div>&nbsp;</div>
                                    <Typography style={{fontWeight: "bold"}}>{props.title}</Typography>
                                </div>
                            }
                        </div>

                        <div style={{display: "flex", flexDirection: "row", alignItems: "center"}}>

                            <ArgoCheckboxHook
                                fileName="GroupEditor"
                                fieldName="isLikert"
                                label="Group is Likert"
                                onChange={onCheckboxChange}
                                checked={template.questionGroupType === "LIKERT"}
                                disabled={!props.createMode || ((questionKeys || []).length > 0) || ((answerOptionTemplates || []).length > 0)}
                                width="250px"
                                paddingTop="20px"
                            />
                        </div>

                        <div style={{color: 'red', fontSize: 'small'}}>{state.likertErrorText}</div>

                        <div>&nbsp;</div>

                        <ArgoTextEntryWithCustomOverrideHook
                            templateType={TEMPLATE.TYPE.GROUP}
                            textEntryType={TEMPLATE.TEXT_TYPE.GROUP.NAME}
                            fieldName="name"
                            fieldTextObjName="nameDisplay"
                            label="Group Name"
                            labelCustom="Display (Name)"
                            value={template.name}
                            maxlength={255}
                            errorText={state.nameErrorText}
                            initTo={state.nameDisplay.selected}
                            initToObj={state.nameDisplay.metadataValueObj}
                            propsObj={Object.assign({metadataTag: portalShared.CUSTOM_DDO_NAME}, propsObj)}
                            showQuestionNum={true}
                            autoFocus={true}
                        />

                        <div id="GroupEditor-qualifier-container-div"
                             style={{display: "flex", alignItems: "left", width: "100%"}}>

                            <ArgoTextFieldHook
                                fileName="GroupEditor"
                                fieldName="nameQualifier"
                                label="Name Qualifier"
                                value={template.nameQualifier}
                                maxLength={255}
                                multiline={true}
                                onChange={onTextChange}
                                width="96%"
                                divContainerHeight="52px"
                            />

                            <div style={{width: "83px"}}>&nbsp;</div>

                        </div>

                        <div>&nbsp;</div>
                        <div>&nbsp;</div>

                        <ArgoTextEntryWithCustomOverrideHook
                            templateType={TEMPLATE.TYPE.GROUP}
                            textEntryType={TEMPLATE.TEXT_TYPE.GROUP.DESC}
                            fieldName="description"
                            fieldTextObjName="descDisplay"
                            label="Description/Instructions"
                            labelCustom="Display (Description)"
                            value={template.description}
                            maxlength={255}
                            initTo={state.descDisplay.selected}
                            initToObj={state.descDisplay.metadataValueObj}
                            propsObj={Object.assign({metadataTag: portalShared.CUSTOM_DDO_DESC}, propsObj)}
                            showIndent={true}
                        />

                        <div id="GroupEditor-metadata-container-div"
                             style={{width: "100%"}}>
                            <ArgoMetadataEditorHook
                                entityType={metadataTemplateTypes.GroupTemplate}
                                template={template}
                                onChangeCallback={metadataChanged}
                                metadata={template.metadata}
                                errorText={state.metadataErrorText}
                                setMetadataErrorText={setMetadataErrorText}
                                hidden={true}
                            />
                        </div>

                        <ArgoLikertAnswerGeneratorHook
                            groupTemplateMode={true}
                            fileName="GroupEditor"
                            componentMode="likert-answers"
                            templateType={TEMPLATE.TYPE.GROUP}
                            templateTextType={TEMPLATE.TEXT_TYPE.GROUP.LIKERT_TITLES}
                            template={template}
                            isLikert={template.questionGroupType === "LIKERT"}
                            displayAsLikert={template.questionGroupType === "LIKERT"}
                            answerOptionTemplates={answerOptionTemplates}
                            answerErrorCallback={answerErrorCallback}
                            templateUpdateFunction={(t, d) => dispatch(updateGroupTemplate(t, d))}
                            setMetadataErrorText={setMetadataErrorText}
                            questionDepth={props.questionDepth}
                            setStateCallback={setStateCallback}
                            customMetadataKey={portalShared.CUSTOM_DDO_GRP_FORMAT}
                            propsObj={propsObj}
                            createMode={props.createMode}
                        />

                        {(template.questionGroupType === "LIKERT") && ((answerOptionTemplates || []).length > 0 && template.metadata.hasOwnProperty(portalShared.CUSTOM_DDO_GRP_FORMAT)) ?
                            <ArgoCustomLikertInputStylingHook
                                templateType={TEMPLATE.TYPE.GROUP}
                                template={template}
                                depth={props.questionDepth}
                                customMetadataTag={portalShared.CUSTOM_DDO_GRP_FORMAT}
                                templateUpdateFunction={(t, d) => dispatch(updateGroupTemplate(t, d))}
                                inputType={answerOptionTemplates[0].metadata[icon.ICON_TYPE]}
                                propsObj={propsObj}
                            />
                            : ""}

                        <ArgoSelectExistingHook
                            mode={TEMPLATE.TYPE.QUESTION}
                            editorName="GroupEditor"
                            useTypeFilter={true}
                            likert={template.questionGroupType === "LIKERT"}
                            paddingRight="0px"
                            onAddTemplate={addSelectedQuestion}
                            width={state.isLikert ? 220 : 160}
                            openEditorCallBack={toggleIsThisAnExistingQuestion}
                            createMode={props.createMode}
                            parentTemplate={template}
                        />

                        <div>&nbsp;</div>

                        {/*// ===========================================================================================================*/}
                        {/*// RELATED SHOW GROUPS LIST */}
                        {/*// ===========================================================================================================*/}

                        <QuestionViewerExpandableHook
                            parentType={TEMPLATE.TYPE.GROUP}
                            parentTemplate={template}
                            parentName={"GroupEditor"}
                            questionKeys={questionKeys}
                            questionTemplate={questionTemplates}
                            setStateCallback={setStateCallback}
                            hidePortalPreview={template.questionGroupType === "LIKERT"}
                            isLikert={template.questionGroupType === "LIKERT"}
                            removeQuestionCallback={removeQuestion}
                        />

                        {/*// ===========================================================================================================*/}
                        {/*// RELATED SHOW GROUPS LIST */}
                        {/*// ===========================================================================================================*/}

                        <GroupViewerExpandableHook
                            parentName="GroupEditor"
                            parentKey=""
                            groupKeys={props.createMode ? (template && template.hasOwnProperty("showGroupKeys") ? template.showGroupKeys : []) : ((templateOriginal && templateOriginal.hasOwnProperty("showGroupKeys")) ? templateOriginal.showGroupKeys : [])}
                            setStateCallback={setStateCallback}
                            hidePortalPreview={true}
                            questionTemplate={questionTemplates}
                            showIconButtonReorder={false}
                            showIconButtonEdit={false}
                            showIconButtonRemove={false}
                            isShowGroups={true}
                        />

                        {/*// ===========================================================================================================*/}
                        {/*// PORTAL PREVIEW */}
                        {/*// ===========================================================================================================*/}

                        <div id="GroupEditor-space-above-portal-preview" style={{height: "20px"}}>&nbsp;</div>

                        <ArgoPortalPreviewHook
                            calledBy="GroupEditor"
                            createMode={props.createMode}
                            templateType={TEMPLATE.TYPE.GROUP}
                            template={groupTemplateClone}
                        />

                    </div>
                </div>

            </div> : <div id="GroupEditor-container-outer-div"/>
    );
};

GroupEditor.defaultProps = {
    createMode: false,
    title: "",
    groupAddShowGroupMode: false,
    groupExpanderEdit: false,
    groupExpanderSetStateCallback: function () {
    },
    parentEditorOrigin: null,
    scrollTo: "",
    width: "100%"
};

GroupEditor.propTypes = {
    scrollTo: PropTypes.string,
    createMode: PropTypes.bool,
    title: PropTypes.string,
    save: PropTypes.func,
    cancel: PropTypes.func,
    templateId: PropTypes.number,
    templateKey: PropTypes.string,
    showCreateToggle: PropTypes.func,
    createInitialTemplate: PropTypes.object,
    groupAddShowGroupMode: PropTypes.bool,
    groupExpanderEdit: PropTypes.bool,
    groupExpanderSetStateCallback: PropTypes.func,
    parentEditorOrigin: PropTypes.object,
    width: PropTypes.string
};

export default GroupEditor;
